import { EmbeddableFlowSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, ButtonWidget, SplitButtonWidget } from '@tableau/widgets';
import * as queryString from 'query-string';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import {
  sendSawConductorAwarenessTelemetry,
  sendShowConductorAwarenessTelemetry
} from '~src/flow/components/ConductorAwarenessTelemetry';
import { RunFlowTaskDefinition } from '~src/flow/contentDefinitions/RunFlowTaskDefinition';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { RequestAccessAction } from '~src/requestAccess/actions/RequestAccessAction';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { ContentSpecificControls } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { FlowSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { SubscriptionDefinition } from '~src/subscription/contentDefinitions/SubscriptionDefinition';

import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { Messages } from '../../codegen/Localize';
import { ISanitizer } from '../../commonInfra/utils/Sanitize';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { PublishFlowAction } from '../actions/PublishFlowAction';
import { FlowOutputStepRunDefinition } from '../contentDefinitions/FlowOutputStepRunDefinition';
import { FlowDetailInfo, FlowDetailWithUsageInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IPlace } from '../models/IPlace';
import { Flows } from '../resources/Flows';
import { FlowAllowedActions } from '../serverApi/types/FlowAllowedActions';
import { FlowSubplaceProps } from '../types/FlowSubplaceProps';
import { openCreateFlowRunTaskDialog } from '../utils/FlowDialogUtils';
import { areFlowParametersEnabled } from '../utils/FlowParametersUtils';
import { showPrepConductorAwareness } from '../utils/PrepConductorAwarenessUtils';
import { CreateTaskMenuDropdownButton,newTaskButton } from './CreateFlowTaskButtons';
import { FlowConnectionsSubplace } from './FlowConnectionsSubplace';
import { flowHasIncompatibleDocumentVersion, FlowOverviewSubplace } from './FlowOverviewSubplace';
import { FlowSchedulesInformationalContent } from './FlowSchedulesInformationalContent';
import { FlowSpaceHeader } from './FlowSpaceHeader';
import { showCreateFlowSubscriptionDialog } from './FlowSubscriptionCreationDialog';

export type FlowSpaceStateProps = {
  readonly catalogingOnSiteEnabled: boolean;
  readonly schedulingFlowsEnabled: boolean;
  readonly urls: SpacesSiteUrls;
  readonly areUsersVisible: boolean;
  readonly flowOutputSubscriptionsEnabledOnSite: boolean;
};

export type FlowSpaceDispatchProps = {
  readonly enterSpace: (flowDetailInfo?: FlowDetailWithUsageInfo) => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
};

export interface FlowSpaceOwnProps {
  readonly routeLocationSearch: string;
  readonly showPublishResult: boolean;

  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailWithUsageInfo;
  readonly flowListItemInfo: FlowListItemInfo;
  readonly projectHierarchy: INamedItemInfo[];

  readonly refreshData: () => void;

  // subplace info
  readonly subplaceName: string;

  // Angular services
  readonly place: IPlace;
  readonly sanitizer: ISanitizer;
  readonly siteUrls: SiteUrls;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
}

export interface FlowSpaceProviderState {
  readonly initialRender: boolean;
}

const InitialState: FlowSpaceProviderState = {
  initialRender: true,
};

let sawConductorAwarenessButton = false;

export type FlowSpaceProps = FlowSpaceStateProps & FlowSpaceDispatchProps & FlowSpaceOwnProps;

function overviewSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return {
    name: 'overview',
    tabLabel: Messages.overview(),
    renderContent: () => <FlowOverviewSubplace {...subplaceProps} />,
    shouldRenderTabContentCount: false
  };
}

function connectionsSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return {
    name: 'connections',
    tabLabel: Messages.connections(),
    renderContent: () => <FlowConnectionsSubplace {...subplaceProps} />,
    shouldRenderTabContentCount: false
  };
}

function scheduledTasksSubspace(subplaceProps: FlowSubplaceProps, schedulingFlowsEnabled: boolean): Subspace {
  const toolbarContentSpecificControlsProps: ContentSpecificControls =
    { beginning: subplaceProps.flowAllowedActions.manageFlow ? renderCreateTaskButton(subplaceProps) : null };
  if (showPrepConductorAwareness()) {
    if (!sawConductorAwarenessButton) {
      sendSawConductorAwarenessTelemetry();
      sawConductorAwarenessButton = true;
    }

    return {
      name: 'scheduledTasks',
      tabLabel: Messages.scheduledTasks(),
      renderContent: () => {
        sendShowConductorAwarenessTelemetry();
        return FlowSchedulesInformationalContent(subplaceProps);
      },
      shouldRenderTabContentCount: false,
      tabTextOnHover: Messages.flowScheduledTasksAvailableWithDataManagement(),
      tabLowEmphesis: true,
    };
  }
  if (!schedulingFlowsEnabled) {
    return null;
  }
    return explorerSubspace({
      name: 'scheduledTasks',
      tabLabel: Messages.scheduledTasks(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      shouldRenderContentCount: false,
      toolbarContentSpecificControls: toolbarContentSpecificControlsProps,
      supportFilterPanel: false,
      hideSelection: false,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.flowTasksEmptyMessageWithPermissions() // TODO w/out perms
      }
    });
}

function lineageSubspace(subplaceProps: FlowSubplaceProps, areUsersVisible: boolean, siteLuid: string): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    renderContent: () =>
      <EmbeddableFlowSubplaceWithoutRouter
        flowVizportalUrlId={subplaceProps.flowDetailInfo.id}
        client={catalogApolloClient}
        siteUrls={subplaceProps.siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
        shouldShowFlowParametersWarning={areFlowParametersEnabled() && subplaceProps.flowDetailInfo.containsParameters}
        telemetryContext={getTelemetryCommonProperties()}
        siteLuid={siteLuid}
        />,
    shouldRenderTabContentCount: false,
  };
}

function runHistorySubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return explorerSubspace({
    name: 'runHistory',
    tabLabel: Messages.runHistory(),
    supportedDisplayModes: FlowOutputStepRunDefinition.supportedDisplayModes,
    shouldRenderContentCount: false,
    supportFilterPanel: true,
    hideSelection: true,
    toolbarVisibility: 'enabled',
    emptyStateProps: {
      primaryMessage: Messages.flowsEmptyRunHistory()
    }
  });
}

function renderCreateSubscriptionButton(id: string, name: string): JSX.Element {
  return (
    <ButtonWidget
      testId={'subscribe-to-flow-button-test-id'}
      buttonType={ButtonType.Outline}
      handleClick={() => { showCreateFlowSubscriptionDialog(id, name); }}
    >
      {Messages.flowSubscriptionsSubscribe()}
    </ButtonWidget>
  );
}

function subscriptionsSubspace(subplaceProps: FlowSubplaceProps): Subspace {
  return explorerSubspace({
    name: 'subscriptions',
    tabLabel: Messages.subscriptions(),
    supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
    shouldRenderContentCount: false,
    toolbarVisibility: 'enabled',
    emptyStateProps: {
      primaryMessage: Messages.flowSubscriptionsEmptyMessage()
    },
    toolbarContentSpecificControls: {
      beginning: subplaceProps.flowAllowedActions.createSubscription ?
        renderCreateSubscriptionButton(subplaceProps.flowDetailInfo.id, subplaceProps.flowDetailInfo.name)
        : null
    },
  });
}

function getFlowSubspaces(
  subplaceProps: FlowSubplaceProps,
  catalogingOnSiteEnabled: boolean,
  schedulingFlowsEnabled: boolean,
  areUsersVisible: boolean,
  flowOutputSubscriptionsEnabledOnSite: boolean
): ReadonlyArray<Subspace> {
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

  if (flowHasIncompatibleDocumentVersion(subplaceProps)) {
    // (mechen) for flow with incompatible doc version, only show overview tab, since
    // there wont be any step/connections information and user cannot schedule/run the flow
    return [overviewSubspace(subplaceProps)];
  }
  return _.compact([
    overviewSubspace(subplaceProps),
    connectionsSubspace(subplaceProps),
    scheduledTasksSubspace(subplaceProps, schedulingFlowsEnabled),
    schedulingFlowsEnabled && runHistorySubspace(subplaceProps),
    schedulingFlowsEnabled && flowOutputSubscriptionsEnabledOnSite && subscriptionsSubspace(subplaceProps),
    catalogingOnSiteEnabled && lineageSubspace(subplaceProps, areUsersVisible, siteLuid),
  ]);
}

function renderCreateTaskButton(subplaceProps: FlowSubplaceProps): JSX.Element {
  const showLinkedTasks: boolean = areLinkedTasksEnabled();
  return (
    showLinkedTasks ?
      <SplitButtonWidget
        button={newTaskButton(subplaceProps.flowDetailInfo, showLinkedTasks)}
        menuButton={<CreateTaskMenuDropdownButton flowDetailInfo={subplaceProps.flowDetailInfo} aria-label={Messages.scheduleTaskMoreOptions()} />}
      />
      :
      newTaskButton(subplaceProps.flowDetailInfo, showLinkedTasks)
  );
}

export function checkFlowUrlParams(flowInfo: FlowDetailInfo) {
  const urlParams = new URLSearchParams(WindowLocationService.getHashQueryString());
  const showTaskDialog = urlParams.get('showTaskDialog');
  if (showTaskDialog && showTaskDialog.toLowerCase() !== 'false') {
    const outputName = urlParams.get('outputName');
    if (outputName) {
      Flows.getOutputStepInfos(flowInfo.id).then((outputStepInfos) => {
        const output = outputStepInfos.find((outputStepInfo) => outputStepInfo.name === outputName);
        openCreateFlowRunTaskDialog(flowInfo, output?.id ?? undefined, showTaskDialog === 'linkedTask');
      });
    } else {
      openCreateFlowRunTaskDialog(flowInfo, undefined, showTaskDialog === 'linkedTask');
    }
  }
}

export class FlowSpace extends React.PureComponent<FlowSpaceProps,FlowSpaceProviderState> {

  private memoizedGetFlowSubspaces = defaultMemoize(getFlowSubspaces);

  constructor(props: FlowSpaceProps) {
    super(props);
    this.state = InitialState;
    props.enterSpace();
  }

  public exploreFlowUrl = (subspaceName?: FlowSubspaceName) => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.flowOverviewUrl(this.props.flowDetailInfo.id);
      case 'connections':
        return this.props.urls.flowConnectionsUrl(this.props.flowDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.flowScheduledTasksUrl(this.props.flowDetailInfo.id);
      case 'runHistory':
        return this.props.urls.flowRunHistoryUrl(this.props.flowDetailInfo.id);
      case 'subscriptions':
        return this.props.urls.flowSubscriptionsUrl(this.props.flowDetailInfo.id);
      case 'lineage':
        return this.props.urls.flowLineageUrl(this.props.flowDetailInfo.id);
    }
  };

  public render() {
    const subplaceProps: FlowSubplaceProps = {
      enterSpace: this.props.enterSpace,
      flowAllowedActions: this.props.flowAllowedActions,
      flowDetailInfo: this.props.flowDetailInfo,
      refreshData: this.props.refreshData,
      sanitizer: this.props.sanitizer,
      siteUrls: this.props.urls,
    };

    const subspaces = this.memoizedGetFlowSubspaces(
      subplaceProps,
      this.props.catalogingOnSiteEnabled,
      this.props.schedulingFlowsEnabled,
      this.props.areUsersVisible,
      this.props.flowOutputSubscriptionsEnabledOnSite
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as FlowSubspaceName}
        urlForSubspace={this.exploreFlowUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'flow'}
        spaceItemName={this.props.flowDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.flowDetailInfo);
    this.updateBreadcrumbs();
    Flows.markRecentlyViewed(this.props.flowDetailInfo.id);

    if (this.props.showPublishResult) {
      const queryParams = queryString.parse(this.props.routeLocationSearch);
      const publishedDataSourcesParam = queryParams[UrlParamKeys.PublishedDataSources];
      const publishedDataSourceUrlNames = publishedDataSourcesParam ? publishedDataSourcesParam.split(',') : [];
      PublishFlowAction.handlePublishedFlow(this.props.flowDetailInfo, publishedDataSourceUrlNames);
    }

    checkFlowUrlParams(this.props.flowDetailInfo);
  }

  public componentDidUpdate(prevProps: FlowSpaceProps) {
    if (this.props.flowDetailInfo !== prevProps.flowDetailInfo ||
      this.props.subplaceName !== prevProps.subplaceName) {
      this.props.enterSpace();
      this.updateBreadcrumbs();
    }
    if (this.props.flowDetailInfo.id !== prevProps.flowDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.flowDetailInfo);
      Flows.markRecentlyViewed(this.props.flowDetailInfo.id);
    }
    if (this.state.initialRender) {
      this.setState({initialRender: false}, () => {
        if (this.props.grantAccessDisplay) {
          RequestAccessAction.handleGrantAccess(this.props.flowDetailInfo, this.props.grantAccessUrl);
          this.resetPreviousUrl();
        }
      });
    }
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.props.flowDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const flowDetailsWithUsage = {
      usageInfo: this.props.flowListItemInfo.usageInfo,
      usageInfoMaxValues: this.props.flowListItemInfo.usageInfoMaxValues,
      ...this.props.flowDetailInfo
    };
    const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

    return (
      <FlowSpaceHeader
        item={flowDetailsWithUsage}
        availableActions={this.props.flowAllowedActions}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
        catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
      />
  );
  }

  private resetPreviousUrl = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  };
}
